import React from 'react';
import { withStyles, Collapse } from '@material-ui/core';
import SwitchWithLabel from '../../../../Dispatch/RouteExecution/DriverPopUp/common/CustomSwitch';
import ToolTip from '@StyledToolTip';
import PromptConfirmationSelectWithLabel from './PromptConfirmationSelectWithLabel';
import DateRangePicker from '../../../../../common/MUI/DateRangePicker';
import { driverConfirmationConstants } from './constants';
import { ConfirmationDateRange } from './utils';
import styles from './styles';

type PromptConfirmationOption = {
  value: number;
  label: string;
};

type DriverConfirmationSectionProps = {
  classes: Record<string, string>;
  isDriverConfirmationRequired: boolean;
  promptConfirmationDays: number | null;
  confirmationDateRange: ConfirmationDateRange;
  onConfirmationRequiredChange: (value: boolean) => void;
  onPromptConfirmationDaysChange: (value: number | null) => void;
  onConfirmationDateRangeChange: (value: ConfirmationDateRange) => void;
  isDriverConfirmationDisabled?: boolean;
  driverConfirmationDisabledTooltip?: string;
  errors?: {
    promptConfirmationDays?: string | null;
    confirmationDateRange?: string | null;
  };
};

const DriverConfirmationSection = ({
  classes,
  isDriverConfirmationRequired,
  promptConfirmationDays,
  confirmationDateRange,
  onConfirmationRequiredChange,
  onPromptConfirmationDaysChange,
  onConfirmationDateRangeChange,
  isDriverConfirmationDisabled = false,
  driverConfirmationDisabledTooltip,
  errors = {}
}: DriverConfirmationSectionProps) => {
  const handlePromptDaysChange = (selectedOption: PromptConfirmationOption | null) => {
    onPromptConfirmationDaysChange(selectedOption?.value ?? null);
  };

  const showConfirmationFields =
    isDriverConfirmationRequired && !isDriverConfirmationDisabled;
  const toggle = (
    <SwitchWithLabel
      id="driverConfirmationRequired"
      rowLabel={driverConfirmationConstants.LABEL.DRIVER_CONFIRMATION_REQUIRED}
      onChange={onConfirmationRequiredChange}
      value={isDriverConfirmationRequired}
      disabled={isDriverConfirmationDisabled}
      customClasses={{ detailsColumnWise: classes.toggleRow }}
    />
  );

  return (
    <div
      className={`${classes.sectionContainer}${
        isDriverConfirmationDisabled ? ` ${classes.sectionDisabled}` : ''
      }`}>
      {isDriverConfirmationDisabled && driverConfirmationDisabledTooltip ? (
        <ToolTip title={driverConfirmationDisabledTooltip}>
          <div className={classes.disabledToggleWrapper}>{toggle}</div>
        </ToolTip>
      ) : (
        toggle
      )}
      {showConfirmationFields && (
        <span className={classes.helperText}>
          {driverConfirmationConstants.LABEL.DRIVER_CONFIRMATION_HELPER}
        </span>
      )}
      <Collapse in={showConfirmationFields} timeout="auto" unmountOnExit>
        <div className={classes.promptConfirmationContainer}>
          <PromptConfirmationSelectWithLabel
            id="promptConfirmationDays"
            label={`${driverConfirmationConstants.LABEL.PROMPT_CONFIRMATION}:`}
            suffix={driverConfirmationConstants.LABEL.PROMPT_CONFIRMATION_SUFFIX}
            value={promptConfirmationDays ?? ''}
            data={driverConfirmationConstants.promptConfirmationDayOptions}
            onChange={handlePromptDaysChange}
            error={errors.promptConfirmationDays}
          />
        </div>
        <DateRangePicker
          labelText={driverConfirmationConstants.LABEL.CONFIRMATION_DISPLAY_RANGE}
          value={confirmationDateRange}
          onChange={onConfirmationDateRangeChange}
          maxRange={driverConfirmationConstants.MAX_CONFIRMATION_DISPLAY_RANGE_DAYS}
          isFutureDateAllowed
          errorInfo={errors.confirmationDateRange}
        />
      </Collapse>
    </div>
  );
};

export default withStyles(styles)(DriverConfirmationSection);
